/**
 * Pure helpers to decide whether a promo modal campaign is active.
 * @param {import('./promoModalConfig').PromoModalCampaign} campaign
 * @param {import('./promoModalConfig').PromoModalMatchContext} ctx
 * @returns {boolean}
 */
export function campaignMatches(campaign, ctx) {
  if (!campaign || campaign.enabled === false) {
    return false;
  }
  const now = ctx.now instanceof Date ? ctx.now : new Date();
  if (campaign.dateRange) {
    const {
      start,
      end
    } = campaign.dateRange;
    if (start || end) {
      const day = formatLocalDate(now);
      if (start && day < start) {
        return false;
      }
      if (end && day > end) {
        return false;
      }
    }
  }
  if (campaign.daysOfWeek && campaign.daysOfWeek.length > 0) {
    const dow = now.getDay();
    if (!campaign.daysOfWeek.includes(dow)) {
      return false;
    }
  }
  if (campaign.hoursLocal) {
    if (!isWithinLocalHours(now, campaign.hoursLocal.start, campaign.hoursLocal.end)) {
      return false;
    }
  }
  if (campaign.pathPatterns && campaign.pathPatterns.length > 0) {
    const path = normalizePathname(ctx.pathname || "");
    const mode = campaign.pathMatchMode || "prefix";
    const ok = campaign.pathPatterns.some(pattern => {
      const p = normalizePathname(pattern);
      if (mode === "exact") {
        return path === p;
      }
      return path === p || path.startsWith(p + "/");
    });
    if (!ok) {
      return false;
    }
  }
  if (campaign.storeLsIds && campaign.storeLsIds.length > 0) {
    const id = ctx.storeLsId;
    if (id == null || !campaign.storeLsIds.includes(Number(id))) {
      return false;
    }
  }
  if (campaign.storeCodes && campaign.storeCodes.length > 0) {
    const storeCode = ctx.storeCode;
    if (!storeCode || !campaign.storeCodes.includes(storeCode)) {
      return false;
    }
  }
  if (campaign.excludeStoreCodes && campaign.excludeStoreCodes.length > 0) {
    const storeCode = ctx.storeCode;
    if (storeCode && campaign.excludeStoreCodes.includes(storeCode)) {
      return false;
    }
  }
  if (campaign.excludeStoreLsIds && campaign.excludeStoreLsIds.length > 0) {
    const id = ctx.storeLsId;
    if (id != null && campaign.excludeStoreLsIds.includes(Number(id))) {
      return false;
    }
  }
  if (campaign.excludeStoreViewOrders && campaign.excludeStoreViewOrders.length > 0) {
    const storeViewOrder = ctx.storeViewOrder || ctx.siteStoreViewCode;
    if (storeViewOrder && campaign.excludeStoreViewOrders.includes(storeViewOrder)) {
      return false;
    }
  }
  if (campaign.guestsOnly && ctx.isSignedIn) {
    return false;
  }
  if (campaign.locales && campaign.locales.length > 0) {
    const language = normalizeLanguage(ctx.language);
    if (!language || !campaign.locales.map(normalizeLanguage).includes(language)) {
      return false;
    }
  }
  if (campaign.primaryCta && campaign.primaryCta.action === "add_to_cart" && campaign.primaryCta.needStock) {
    const sku = campaign.primaryCta.sku;
    const availableSkus = Array.isArray(ctx.availableSkus) ? ctx.availableSkus : [];
    if (!sku || !availableSkus.includes(sku)) {
      return false;
    }
  }
  return true;
}

/**
 * @param {import('./promoModalConfig').PromoModalCampaign[]} campaigns
 * @param {import('./promoModalConfig').PromoModalMatchContext} ctx
 * @returns {import('./promoModalConfig').PromoModalCampaign | null}
 */
export function pickFirstMatchingCampaign(campaigns, ctx) {
  if (!Array.isArray(campaigns)) {
    return null;
  }
  const sorted = [...campaigns].sort((a, b) => {
    const pa = a.priority != null ? a.priority : 100;
    const pb = b.priority != null ? b.priority : 100;
    return pa - pb;
  });
  for (const c of sorted) {
    if (campaignMatches(c, ctx)) {
      return c;
    }
  }
  return null;
}
function formatLocalDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return "".concat(y, "-").concat(m, "-").concat(day);
}
function minutesSinceMidnight(d) {
  return d.getHours() * 60 + d.getMinutes();
}
function parseHm(hm) {
  if (!hm || typeof hm !== "string") {
    return null;
  }
  const [h, m] = hm.split(":").map(x => parseInt(x, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) {
    return null;
  }
  return h * 60 + m;
}
function isWithinLocalHours(now, startHm, endHm) {
  const cur = minutesSinceMidnight(now);
  const start = parseHm(startHm);
  const end = parseHm(endHm);
  if (start == null || end == null) {
    return true;
  }
  if (start <= end) {
    return cur >= start && cur <= end;
  }
  // window across midnight
  return cur >= start || cur <= end;
}
function normalizePathname(pathname) {
  if (!pathname) {
    return "/";
  }
  let p = pathname;
  if (p.length > 1 && p.endsWith("/")) {
    p = p.slice(0, -1);
  }
  return p;
}
function normalizeLanguage(language) {
  if (!language || typeof language !== "string") {
    return null;
  }
  return language.trim().toLowerCase();
}